import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField, FormHelperText } from '@mui/material';
import { Controller, FieldErrors, Control } from 'react-hook-form';
import { translate as t } from 'hooks/useTranslations';
import { FieldOption } from '../../_types/fieldbank';
import SingleSelectOptionManager from '../../../_components/SingleSelectOptionManager';

interface SingleSelectFieldProps {
  control: Control<any>;
  errors: FieldErrors<any>;
  getValues?: any;
  setValue?: any;
  fieldId?: number;
  isEditing?: boolean;
  isDraft?: boolean;
}

const SingleSelectField: React.FC<SingleSelectFieldProps> = ({
  control,
  errors,
  getValues,
  setValue,
  fieldId,
  isEditing = false,
  isDraft = true,
}) => {
  const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([
    { label: '', status: 'Active', isVisible: true },
    { label: '', status: 'Active', isVisible: true },
  ]);

  // Initialize options from form data
  useEffect(() => {
    const formValues = getValues();
    if (formValues?.fieldOptions && Array.isArray(formValues.fieldOptions)) {
      const existingOptions = formValues.fieldOptions.map((option: FieldOption) => ({
        // Preserve all data as received from backend for existing options
        ...option,
        label: option.label || '',
        status: option.status || 'Active',
        isVisible: option.isVisible !== false, // Default to true for field bank
      }));
      setFieldOptions(
        existingOptions.length >= 2
          ? existingOptions
          : [
              ...existingOptions,
              ...Array(2 - existingOptions.length)
                .fill(0)
                .map(() => ({
                  id: null, // New options get id: null
                  label: '',
                  status: 'Active' as 'Active',
                  isVisible: true,
                })),
            ]
      );
    }
  }, [getValues]);

  // Update form values when options change
  useEffect(() => {
    if (setValue) {
      setValue('fieldOptions', fieldOptions);
    }
  }, [fieldOptions, setValue]);

  const handleOptionsChange = (newOptions: FieldOption[]) => {
    // Clear default value if it was using a removed or changed option
    const currentDefaultValue = getValues('defaultValue');
    if (currentDefaultValue) {
      const defaultOptionExists = newOptions.some(
        (opt) => opt.label === currentDefaultValue && opt.status === 'Active' && opt.isVisible !== false
      );
      if (!defaultOptionExists) {
        setValue('defaultValue', '');
      }
    }

    setFieldOptions(newOptions);
  };

  // Get available options for default value dropdown (only active and visible options with labels)
  const availableOptionsForDefault = fieldOptions.filter(
    (opt) => opt.label && opt.label.trim() !== '' && opt.status === 'Active' && opt.isVisible !== false
  );

  return (
    <Stack spacing={3}>
      {/* Field Label */}
      <Controller
        name='label'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('performanceReporting.fieldBank.form.label')}
            required
            fullWidth
            error={!!errors.label}
            helperText={(errors.label?.message as string) || ''}
          />
        )}
      />

      {/* Options Management using shared component */}
      <SingleSelectOptionManager
        options={fieldOptions}
        onChange={handleOptionsChange}
        mode='fieldBank'
        errors={errors}
        isDraft={isDraft}
      />

      {/* Default Value Selection */}
      <Controller
        name='defaultValue'
        control={control}
        render={({ field }) => (
          <FormControl
            fullWidth
            error={!!errors.defaultValue}
          >
            <InputLabel id='default-value-label'>{t('performanceReporting.fieldBank.form.defaultValue')}</InputLabel>
            <Select
              {...field}
              displayEmpty
              value={field.value || ''}
              sx={{
                '& .MuiSelect-select': {
                  fontWeight: 500,
                },
              }}
            >
              <MenuItem
                value=''
                sx={{ fontWeight: 600 }}
              >
                <span>Choose default value</span>
              </MenuItem>
              {availableOptionsForDefault.map((option, index) => (
                <MenuItem
                  key={index}
                  value={option.label}
                  sx={{ fontWeight: 500 }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.defaultValue && <FormHelperText>{errors.defaultValue.message as string}</FormHelperText>}
          </FormControl>
        )}
      />
    </Stack>
  );
};

export default SingleSelectField;
